import { useEffect, useState } from "react";
import { Eye, Users, CalendarDays, Activity, Globe, Loader2, RefreshCw } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { fetchAnalytics } from "../lib/api";

function flag(code) {
  if (!code || code.length !== 2 || code === "LO") return "🌐";
  try {
    return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)));
  } catch {
    return "🌐";
  }
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setData(await fetchAnalytics()); }
    catch { toast.error("Failed to load analytics"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  if (!data) return null;

  const maxCountry = Math.max(1, ...data.by_country.map((c) => c.count));

  return (
    <div data-testid="admin-analytics">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Analytics</h1>
          <p className="mt-1 text-sm text-muted-foreground">Website traffic and visitor locations.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-muted" data-testid="analytics-refresh-btn"><RefreshCw className="h-4 w-4" /> Refresh</button>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={Eye} label="Total Page Views" value={data.total} />
        <Stat icon={Users} label="Unique Visitors" value={data.unique_visitors} accent />
        <Stat icon={CalendarDays} label="Last 7 Days" value={data.last_7_days} />
        <Stat icon={Activity} label="Today" value={data.today} />
      </div>

      <div className="mt-6 rounded-2xl border border-border bg-card p-5">
        <h3 className="font-heading font-semibold">Page views — last 14 days</h3>
        <div className="mt-4 h-72" data-testid="analytics-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.by_day} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(150 69% 30%)" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="hsl(150 69% 30%)" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
              <Area type="monotone" dataKey="views" stroke="hsl(150 69% 24%)" strokeWidth={2.5} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="flex items-center gap-2 font-heading font-semibold"><Globe className="h-4 w-4 text-primary" /> Visitors by Country</h3>
          {data.by_country.length === 0 ? (
            <p className="mt-6 text-center text-sm text-muted-foreground">No data yet.</p>
          ) : (
            <ul className="mt-4 space-y-3" data-testid="country-list">
              {data.by_country.map((c) => (
                <li key={c.country} data-testid="country-row">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2"><span className="text-base">{flag(c.code)}</span> {c.country}</span>
                    <span className="font-semibold">{c.count}</span>
                  </div>
                  <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${(c.count / maxCountry) * 100}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="font-heading font-semibold">Top Pages</h3>
          {data.top_pages.length === 0 ? (
            <p className="mt-6 text-center text-sm text-muted-foreground">No data yet.</p>
          ) : (
            <ul className="mt-4 divide-y divide-border">
              {data.top_pages.map((p) => (
                <li key={p.path} className="flex items-center justify-between py-2.5 text-sm">
                  <span className="truncate font-medium">{p.path === "/" ? "/ (Home)" : p.path}</span>
                  <span className="rounded-full bg-secondary/10 px-2.5 py-0.5 text-xs font-semibold text-secondary">{p.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <p className="mt-4 text-xs text-muted-foreground">Note: country is detected from visitor IP. Local/preview visits show as "Local".</p>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${accent ? "bg-accent/15 text-accent" : "bg-primary/10 text-primary"}`}><Icon className="h-5 w-5" /></span>
      <p className="mt-3 font-heading text-2xl font-bold">{value.toLocaleString()}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
